/* eslint-disable max-len */

import { useTranslation } from 'react-i18next';
import {
  capitalizeFirstLetterEveryword
} from '../../../shared/stringFunctions';
import { TXT_ADD_MACHINE, TXT_MACHINE } from '../../../translations/translationConstants';
import {
  TXT_SEARCH_BY_NAME
} from '../../../translations/translationConstants';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
  addMachineInput,
  deleteMachineInput,
  getLithologiesInput,
  getMachineOutput,
  GET_MACHINE,
  updateMachineInput
} from '../../../api';
import { useEffect } from 'react';
import { useState } from 'react';
import { ManageMachinesTable } from './ManageMachineTable';
import {
  DeleteConfirmation,
  ExportToExcel,
  globalPageSizeOptions,
  PaginationBar
} from '../../../components';
import { machine } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import {
  ADD_MACHINE,
  DELETE_MACHINE,
  UPDATE_MACHINE
} from '../../../api';
import { ManageMachineEditor } from './ManagerMachineEditor';

export const ManageMachines = () => {
  const { t } = useTranslation();
  const { isSignedIn } = useAuthServices();

  const [txtSearch, setTxtSearch] = useState('');
  const [alterationData, setAlterationData] = useState<machine[] | undefined>([]);

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const [selectedMachine, setSelectedMachine] = useState<machine | undefined>(undefined);
  const [getMachines, { loading, data, error, refetch }] = useLazyQuery<getMachineOutput>(GET_MACHINE);
  const [getAllAlterations] = useLazyQuery<getMachineOutput>(GET_MACHINE, {
    onCompleted: (data) => setAlterationData(data.machines.items)
  });

  useEffect(() => {
    getMachines();
    getAllAlterations();
  }, []);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);

  const [openEditor, setOpenEditor] = useState(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const fetchMachines = () => {
    const variables: getLithologiesInput = {
      code: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize
    };
    getMachines({
      variables
    });
  };

  const fetch = (resetPage?: boolean) => {
    if (!!resetPage && pageNumber !== 1) {
      setPageNumber(1);
    } else {
      fetchMachines();
    }
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetch();
  }, [pageNumber]);

  useEffect(() => {
    fetch(true);
  }, [pageSize, txtSearch]);

  const openEditMachineEditor = (machine: machine) => {
    setSelectedMachine(machine);
    setOpenEditor(true);
  };

  const openDeleteMachineConfirmation = (machine: machine) => {
    setSelectedMachine(machine);
    setOpenDeleteConfirmation(true);
  };

  const openAddMachineEditor = () => {
    setSelectedMachine(undefined);
    setOpenEditor(true);
  };

  const closeEditor = () => {
    setOpenEditor(false);
  };

  const afterAddingOrUpdatingMachine = () => {
    setOpenEditor(false);
    if (refetch) {
      refetch();
    }
  };

  const handleAddingOrUpdatingMachineError = (error: ApolloError) => {
    if (error.graphQLErrors.length > 0 &&
      !!error.graphQLErrors[0].extensions) {
      setSavingError(error.graphQLErrors[0].extensions['message']);
    } else {
      setSavingError(error.message);
    }
  };

  const [addMachine, addMachineOperation] = useMutation<machine>(ADD_MACHINE, {
    onError: (e) => handleAddingOrUpdatingMachineError(e),
    onCompleted: (result) => afterAddingOrUpdatingMachine()
  });

  const [updateMachine, updateMachineOperation] = useMutation<machine>(UPDATE_MACHINE, {
    onError: (e) => handleAddingOrUpdatingMachineError(e),
    onCompleted: (result) => afterAddingOrUpdatingMachine()
  });

  const onEditorSave = (payload: addMachineInput | updateMachineInput) => {
    if (!selectedMachine) {
      addMachine({
        variables: {
          input: payload
        }
      });
    } else {
      updateMachine({
        variables: {
          input: payload
        }
      });
    }
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteMachine, deleteMachineOperation] = useMutation<boolean>(DELETE_MACHINE, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.machines.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteMachineInput = {
      id: selectedMachine!.id!
    };
    deleteMachine({
      variables: {
        input
      }
    });
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_MACHINE))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            <ManageMachineEditor
              machine={selectedMachine}
              onClose={closeEditor}
              onSave={onEditorSave}
              isSaving={
                !selectedMachine ? addMachineOperation.loading :
                  updateMachineOperation.loading
              }
              savingError={savingError}
            />
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteMachineOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedMachine?.label || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-md-6'>
                      <button
                        className='btn btn-primary'
                        data-target="#modal-machine-editor"
                        data-toggle="modal"
                        disabled={loading}
                        onClick={openAddMachineEditor}>
                        {t(TXT_ADD_MACHINE).toUpperCase()}
                      </button>
                    </div>
                    <div className="col-md-6" style={{ textAlign: 'right' }}>
                      <ExportToExcel filename={TXT_MACHINE} data={alterationData} />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManageMachinesTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={openEditMachineEditor}
                    onDelete={openDeleteMachineConfirmation}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.machines.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
